import { useState, type FormEvent } from 'react';
import { ApiError, changePassword } from '../api';
import { MIN_PASSWORD_LENGTH, newPasswordProblem } from '../password';
import { PasswordField } from './PasswordField';
import { StrengthMeter } from './StrengthMeter';

/** Settings → Password: current + new twice. This device stays signed in; the others are signed out. */
export function ChangePassword({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);

  const close = () => {
    setOpen(false);
    setCurrent('');
    setPassword('');
    setRepeat('');
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = !current ? 'Enter your current password.' : newPasswordProblem(password, repeat);
    if (problem) {
      setMessage({ tone: 'bad', text: problem });
      return;
    }
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await changePassword(current, password);
      close();
      setMessage({
        tone: 'ok',
        text:
          r.otherDevicesSignedOut > 0
            ? 'Password changed. Your other devices were signed out.'
            : 'Password changed.',
      });
    } catch (err) {
      setMessage({ tone: 'bad', text: err instanceof ApiError ? err.message : 'Couldn’t change the password. Try again.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="setting setting-stack">
      <div className="setting-head">
        <div className="setting-text">
          <span className="setting-name">Password</span>
          <span className="setting-help">You log in as {email}.</span>
        </div>
        {!open && (
          <button
            className="btn btn-small"
            type="button"
            onClick={() => {
              setMessage(null);
              setOpen(true);
            }}
          >
            Change
          </button>
        )}
      </div>
      {open && (
        <form className="password-form" onSubmit={submit} noValidate>
          <input type="email" name="email" autoComplete="username" value={email} readOnly hidden />
          <PasswordField
            label="Current password"
            name="current-password"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={setCurrent}
          />
          <PasswordField label="New password" name="new-password" autoComplete="new-password" value={password} onChange={setPassword}>
            <StrengthMeter password={password} email={email} />
          </PasswordField>
          <PasswordField
            label="Repeat new password"
            name="repeat-password"
            autoComplete="new-password"
            value={repeat}
            onChange={setRepeat}
          />
          <p className="setting-note">
            At least {MIN_PASSWORD_LENGTH} characters; a few unrelated words work well. You stay signed in here; your other devices are signed out.
          </p>
          <div className="password-actions">
            <button className="btn btn-quiet btn-small" type="button" onClick={() => { close(); setMessage(null); }} disabled={busy}>
              Cancel
            </button>
            <button className="btn btn-primary btn-small" type="submit" disabled={busy || !current || !password || !repeat}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </form>
      )}
      {message && (
        <p className={`setting-msg setting-msg-${message.tone}`} role={message.tone === 'bad' ? 'alert' : 'status'}>
          {message.text}
        </p>
      )}
    </div>
  );
}
